import React from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import {
    INSTITUTION_KEYS,
    institutionLabel,
} from '@/shared/constants/institutions';
import { CATEGORY_LABELS, STATUS_LABELS } from '@/shared/constants/listings';
import { REQUEST_STATUS_LABELS } from '@/shared/constants/requests';

const STATUS_KEYS = Object.keys(STATUS_LABELS);

function ReportSection({ id, title, children, className = 'mb-4' }) {
    return (
        <section aria-labelledby={id} className={className}>
            <h3 id={id} className="h6">
                {title}
            </h3>
            {children}
        </section>
    );
}

/**
 * A table of one labelled number per row.
 *
 * The label is a row header rather than a cell, so a screen reader reading a
 * count says what it counts.
 */
function CountTable({ heading, rows }) {
    return (
        <Table bordered size="sm" className="afe-count-table">
            <thead>
                <tr>
                    <th scope="col">{heading}</th>
                    <th scope="col" className="text-end">
                        Count
                    </th>
                </tr>
            </thead>
            <tbody>
                {rows.map(([label, value]) => (
                    <tr key={label}>
                        <th scope="row">{label}</th>
                        <td className="text-end">{value}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );
}

/**
 * The all-time counts: listings, exchange outcomes, requests, and members.
 * They are totals over the life of the exchange and do not follow the date
 * range.
 */
function CountTables({ listings, exchanges, requests, members }) {
    return (
        <>
            <ReportSection id="report-listings" title="Listings">
                {/*
                  Seven columns do not fit a narrow screen, so the table
                  scrolls inside its own box (WCAG 1.4.10). A box that scrolls
                  must take keyboard focus (WCAG 2.1.1); the region role and
                  label give that tab stop a name distinct from the section's.
                */}
                <div
                    className="table-responsive"
                    tabIndex={0}
                    role="region"
                    aria-label="Listings by institution and status"
                >
                    <Table bordered size="sm" className="afe-count-table">
                        <thead>
                            <tr>
                                <th scope="col">Institution</th>
                                {STATUS_KEYS.map((status) => (
                                    <th
                                        key={status}
                                        scope="col"
                                        className="text-end"
                                    >
                                        {STATUS_LABELS[status]}
                                    </th>
                                ))}
                                <th scope="col" className="text-end">
                                    Total
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {INSTITUTION_KEYS.map((key) => {
                                const counts = listings.byInstitution[key];
                                const rowTotal = STATUS_KEYS.reduce(
                                    (sum, status) => sum + counts[status],
                                    0
                                );
                                return (
                                    <tr key={key}>
                                        <th scope="row">
                                            {institutionLabel(key)}
                                        </th>
                                        {STATUS_KEYS.map((status) => (
                                            <td
                                                key={status}
                                                className="text-end"
                                            >
                                                {counts[status]}
                                            </td>
                                        ))}
                                        <td className="text-end fw-semibold">
                                            {rowTotal}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
                <p className="text-muted small">
                    {listings.total} listings posted in all.
                </p>
            </ReportSection>
            {/* The four short tables share one grid, so each is the same
                width as its neighbours instead of the width of its longest
                label. */}
            <Row xs={1} md={2} xl={4} className="g-3">
                <Col>
                    <ReportSection
                        id="report-categories"
                        title="Categories"
                        className="afe-count-card"
                    >
                        <CountTable
                            heading="Category"
                            rows={Object.entries(CATEGORY_LABELS).map(
                                ([key, label]) => [
                                    label,
                                    listings.byCategory[key],
                                ]
                            )}
                        />
                    </ReportSection>
                </Col>
                <Col>
                    <ReportSection
                        id="report-exchanges"
                        title="Exchanges"
                        className="afe-count-card"
                    >
                        <CountTable
                            heading="Outcome"
                            rows={[
                                ['Completed', exchanges.completed],
                                ['Transferred', exchanges.transferred],
                                ['Withdrawn', exchanges.withdrawn],
                            ]}
                        />
                        <p className="text-muted small">
                            A withdrawn exchange is an item that never moved, so
                            it is counted as completed and left out of every
                            pound and dollar figure.
                        </p>
                    </ReportSection>
                </Col>
                <Col>
                    <ReportSection
                        id="report-requests"
                        title="Requests"
                        className="afe-count-card"
                    >
                        <CountTable
                            heading="Status"
                            rows={Object.entries(REQUEST_STATUS_LABELS).map(
                                ([key, label]) => [
                                    label,
                                    requests.byStatus[key],
                                ]
                            )}
                        />
                    </ReportSection>
                </Col>
                <Col>
                    <ReportSection
                        id="report-members"
                        title="Members"
                        className="afe-count-card"
                    >
                        <CountTable
                            heading="Institution"
                            rows={[
                                ...INSTITUTION_KEYS.map((key) => [
                                    institutionLabel(key),
                                    members.byInstitution[key],
                                ]),
                                ['All institutions', members.total],
                            ]}
                        />
                    </ReportSection>
                </Col>
            </Row>
        </>
    );
}

export default CountTables;
